import _ from 'lodash';
import React from 'react';
import AppLink from '../components/common/AppLink/AppLink';
import DrawerLink from '../components/common/drawers/ModuleDrawer/DrawerLink';
import { archiveFilter, tagFilter } from '../components/common/filters/consts';
import { IFilter } from '../components/common/filters/interfaces';
import PreferencesPopover from '../components/common/popover/PreferencesPopover';
import { tagToDisplay } from '../types/Tag';
import { EventViewSetting } from '../types/User';
import { ROUTE_PATHS } from '../routes/Routes';
import { EventAvailableColumns } from '../services/react-query/events';
import { Event, EventStatus } from '../types/Event';
import { PREFERENCES_VALID_KEYS } from '../types/Preference';
import { UserConfigurableModule } from '../types/User';
import { AvailableColumn } from '../uikit/itable/ITable';
import StatusPill from '../uikit/pills/StatusPill';
import { humanizeDate, humanizeEventDateTimes, humanizeTimestamp, singleDateEvent } from './dateUtils';
import { mapOptionsFromPreferences } from './filterUtils';
import { useGetAllPreferences } from '../services/react-query/preferences';
import { useGetAllEventTemplate } from '../services/react-query/event_templates';

export const noTzIsoOptions = {
  suppressSeconds: true,
  suppressMilliseconds: true,
  includeOffset: false,
  includePrefix: false,
};

export const EventColumns: AvailableColumn<Event, EventAvailableColumns>[] = [
  {
    key: 'name',
    label: 'Name',
    render: (event) => (
      <DrawerLink module={UserConfigurableModule.EVENTS} uuid={event.uuid}>
        {event.display_name}
      </DrawerLink>
    ),
  },
  {
    key: 'status',
    label: 'Status',
    render: (event) => event.status && <StatusPill status={event.status} />,
  },
  {
    key: 'event_template_name',
    label: 'Template',
    render: (event) => <AppLink to={`/${ROUTE_PATHS.EVENT_TEMPLATES}/${event.event_template_uuid}`}>{event.event_template_name}</AppLink>,
  },
  {
    key: 'type_value',
    label: 'Event Type',
    render: (event) => <AppLink to={`/${ROUTE_PATHS.EVENTS}/${event.uuid}`}>{event.type_value}</AppLink>,
  },
  {
    key: 'location_value',
    label: 'Location',
    render: (event) => event.location_value,
  },
  {
    key: 'start_at',
    label: 'Start At',
    render: (event) => {
      if (singleDateEvent(event)) {
        return humanizeDate(event.start_at);
      } else {
        return humanizeTimestamp(event.start_at);
      }
    },
  },
  {
    key: 'end_at',
    label: 'End At',
    render: (event) => {
      if (singleDateEvent(event)) {
        return humanizeDate(event.start_at);
      } else {
        return humanizeTimestamp(event.end_at);
      }
    },
  },
  {
    key: 'date',
    label: 'Date',
    render: (event) => humanizeEventDateTimes(event),
    sortable: false,
  },
  {
    key: 'account_name',
    label: 'Account Name',
    render: (event) => (
      <DrawerLink module={UserConfigurableModule.ACCOUNTS} uuid={event.account_uuid}>
        {event.account_name}
      </DrawerLink>
    ),
  },
  {
    key: 'account_code',
    label: 'Account Code',
    render: (event) => (
      <DrawerLink module={UserConfigurableModule.ACCOUNTS} uuid={event.account_uuid}>
        {event.account_code}
      </DrawerLink>
    ),
  },
  {
    key: 'tags',
    label: 'Tags',
    render: (event) => <PreferencesPopover values={_.map(event.tags, tagToDisplay)} />,
    sortable: false,
  },
];

export const EVENT_STATUS_OPTIONS: { label: string; value: string }[] = [];
_.forIn(EventStatus, (value) =>
  EVENT_STATUS_OPTIONS.push({
    label: value,
    value,
  })
);
export const useEventFilters = (eventListView: EventViewSetting): IFilter[] => {
  const { data: preferenceData } = useGetAllPreferences({
    params: {
      filters: {
        keys: [PREFERENCES_VALID_KEYS.EVENT_TYPE, PREFERENCES_VALID_KEYS.EVENT_LOCATION],
      },
    },
  });

  const { data: eventTemplates } = useGetAllEventTemplate({ params: { per_page: 0 } });

  const filters: IFilter[] = [
    {
      label: 'Event Type',
      key: PREFERENCES_VALID_KEYS.EVENT_TYPE,
      type: 'dropdown',
      options: mapOptionsFromPreferences(preferenceData || [], PREFERENCES_VALID_KEYS.EVENT_TYPE),
    },
    {
      label: 'Event Location',
      key: PREFERENCES_VALID_KEYS.EVENT_LOCATION,
      type: 'dropdown',
      options: mapOptionsFromPreferences(preferenceData || [], PREFERENCES_VALID_KEYS.EVENT_LOCATION),
    },
    {
      label: 'Event Status',
      key: 'status',
      type: 'dropdown',
      options: EVENT_STATUS_OPTIONS,
    },
    {
      label: 'Event Template',
      key: 'event_template',
      type: 'dropdown',
      options: _.map(eventTemplates?.data, (template) => ({ label: template.name, value: template.uuid })),
    },
    {
      label: 'Account',
      key: 'account',
      type: 'account',
    },
    {
      label: 'Account Manager',
      key: 'account_manager',
      type: 'employee',
    },
  ];
  if (eventListView == 'table') {
    filters.push({
      label: 'Date',
      key: 'date_range',
      type: 'date',
    });
  }
  filters.push(tagFilter);
  filters.push(archiveFilter);
  return filters;
};
